import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Sparkles, Crown, ArrowLeft, ArrowRight, LogIn } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

/**
 * Returns true if user has access (admin or active subscription).
 */
export const hasAccess = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const sub = user.subscription || {};
  if (!sub.plan_id) return false;
  if (sub.status && sub.status !== 'active') return false;
  // Optional: check expires_at
  if (sub.expires_at) {
    try {
      const exp = new Date(sub.expires_at);
      if (!isNaN(exp.getTime()) && exp < new Date()) return false;
    } catch {}
  }
  return true;
};

/**
 * Wraps content; if user has no access, shows a locked screen instead.
 */
const LockedGate = ({ pageTitle, pageDesc, children }) => {
  const { lang, isRTL } = useLang();
  const { user, loading } = useAuth();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const L = (ar, en) => tx(lang, ar, en);

  if (loading) {
    return (
      <main className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  if (hasAccess(user)) {
    return children;
  }

  // Locked state
  const isGuest = !user;

  return (
    <main className="pt-24 pb-16 min-h-screen relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-32 left-1/4 w-[500px] h-[500px] bg-blue-50 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">{pageTitle}</h1>
          <p className="text-slate-500">{pageDesc}</p>
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-blue-50 via-white to-slate-50 border-2 border-blue-200 p-7 md:p-10 text-center shadow-2xl shadow-slate-200/70 relative overflow-hidden">
          {/* Lock icon */}
          <div className="relative inline-flex">
            <div className="absolute inset-0 bg-amber-400/30 blur-2xl rounded-full" />
            <div className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-2xl shadow-amber-500/40 mb-6 mx-auto">
              <Lock className="w-12 h-12 text-slate-900" strokeWidth={2.5} />
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-600 text-xs font-bold mb-4">
            <Crown className="w-3.5 h-3.5" />
            <span>{L('محتوى حصري للمشتركين', 'Subscribers Only Content')}</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-3">
            {isGuest
              ? L('قم بالاشتراك للوصول إلى المحتوى', 'Subscribe to Access This Content')
              : L('يحتاج هذا المحتوى لاشتراك فعّال', 'This Content Requires Active Subscription')}
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            {L(
              'احصل على وصول كامل لجميع الكورسات والفيديوهات والمقالات الحصرية. ابدأ رحلتك التعليمية الآن!',
              'Get full access to all exclusive courses, videos, and articles. Start your learning journey now!'
            )}
          </p>

          {/* Feature highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto mb-8 text-start">
            {[
              { Ic: Sparkles, ar: '+70 كورس احترافي', en: '+70 Pro Courses' },
              { Ic: Crown, ar: 'شهادة معتمدة', en: 'Certified' },
              { Ic: Lock, ar: 'محتوى حصري', en: 'Exclusive Content' },
            ].map((f, i) => (
              <div key={i} className={`flex items-center gap-2 rounded-xl bg-slate-100 border border-slate-200 p-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                  <f.Ic className="w-4 h-4 text-amber-600" />
                </div>
                <span className="text-slate-700 text-sm">{lang === 'ar' ? f.ar : f.en}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/subscription" data-testid="locked-gate-subscribe" className="btn-subscribe h-12 px-8 text-base">
                <Crown className="w-5 h-5" />
                {L('عرض الباقات والاشتراك', 'View Plans & Subscribe')}
                <Arrow className="w-4 h-4" />
            </Link>
            {isGuest && (
              <Link to="/login">
                <Button variant="outline" className="border-blue-200 text-slate-800 hover:bg-slate-100 h-13 px-7 text-base font-semibold bg-transparent">
                  <LogIn className="w-4 h-4 me-2" />
                  {L('تسجيل الدخول', 'Log In')}
                </Button>
              </Link>
            )}
          </div>

          <div className="mt-6 text-xs text-slate-500">
            {L('ضمان استرداد المال خلال 7 أيام • إلغاء في أي وقت', '7-day money-back guarantee • Cancel anytime')}
          </div>
        </div>

        {/* Blurred preview hint */}
        <div className="mt-10 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/80 to-slate-50 pointer-events-none z-10" />
          <div className="opacity-30 pointer-events-none select-none blur-md">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="rounded-2xl bg-white border border-slate-200 h-48" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LockedGate;
